import { PartialRecord } from "@/types/common";
import { Identifier } from "@/types/domain";
import { AnyTranslationsFn } from "@/types/locale";
import { MenuItem } from "@/types/ui";
import { UseRouteInfo } from "@hooks/pages/use-route-info";
import { buildSidebarMenuItem } from "@hooks/use-feature-menu";
import { removeField, removeUndefined } from "@lib/common";
import { domainTools } from "@lib/domain";
import { rightsActions } from "@lib/rights";
import { AccessToken, UserTokenValue } from "@features/auth/types/access-token";
import { EventEntity, EventModuleOutputView, EventOutputView } from "@features/events/types/event";
import {
  EventEntityRightsTypes,
  EventModuleEntityRightsTypes,
} from "@features/events/types/rigths";
import routes from "@features/routes";
import { isProductionEnvironment } from "@lib/environment";

const addRights = (event: EventOutputView): EventEntity =>
  rightsActions.parseActions<EventOutputView, EventEntityRightsTypes, EventEntity>(
    event,
    (actions, fns) => ({
      ...fns.canEntityRights(actions, "event"),
      canListMembers: fns.canList(actions, "team/event-member"),
      canArchive: fns.can(actions, "event", "archive"),
      canAddMember: fns.can(actions, "team/event-member", "register"),
      canExchangeExport: fns.can(actions, "event/exchange", "export"),
      canExchangeImport: fns.can(actions, "event/exchange", "import"),
      canProposalAssignmentCandidates: fns.can(
        actions,
        "event/program/proposal/assignment",
        "candidates",
      ),
    }),
  );

const addRightsToModule = (module: EventModuleOutputView): never =>
  rightsActions.parseActions<EventModuleOutputView, EventModuleEntityRightsTypes, never>(
    module,
    (actions, fns) => ({
      // Schedule modules
      canViewScheduleRegularModule: fns.can(actions, "event/schedule-editor", "access"),
      canViewScheduleBroadcastModule: fns.can(actions, "event/schedule-broadcast", "access"),
      canViewScheduleSupportModule: fns.can(actions, "event/schedule-support", "access"),
      canViewScheduleModule: fns.can(actions, "event/schedule", "access"),
      canListScheduleVersions: fns.canList(actions, "event/schedule/version"),
      canCreateScheduleModerator: fns.canCreate(actions, "event/schedule/moderator"),
      canListScheduleModerators: fns.canList(actions, "event/schedule/moderator"),
      canCreateActivityModerator: fns.canCreate(actions, "event/activity/moderator"),
      canListActivityModerators: fns.canList(actions, "event/activity/moderator"),
      canCreateScheduleVersion: fns.canCreate(actions, "event/schedule/version"),
      canReadPublishedSchedule: fns.canRead(actions, "event/schedule/published"),
      canListSchedulePlanningSections: fns.canList(actions, "event/schedule/planning-section"),
      canCreateSchedulePlanningSection: fns.canCreate(actions, "event/schedule/planning-section"),
      canListProposalsForSchedule: fns.canList(actions, "event/schedule/proposal"),

      // Publications module
      canViewPublicationsModule: fns.can(actions, "event/publication", "access"),
      canListProposalsForPublications: fns.canList(actions, "event/publication/proposal"),

      // Program module
      canViewProgramModule: fns.can(actions, "event/program", "access"),
      canListProposalsForProgram: fns.canList(actions, "event/program/proposal"),

      // Team module
      canViewTeamModule: fns.can(actions, "event/team", "access"),
      canListMembers: fns.canList(actions, "event/team/member"),

      // Reports & Feedback modules
      canViewEventReportModule: fns.can(actions, "event/activity-report", "access"),
      canViewEventActivityFeedbackModule: fns.can(actions, "event/activity-feedback", "access"),
    }),
  );

const concatRightsFromModules = (
  modules?: { rights: PartialRecord<EventModuleEntityRightsTypes, boolean> }[],
) => {
  if (!modules?.length) {
    return {};
  }

  return modules.reduce<PartialRecord<EventModuleEntityRightsTypes, boolean>>((acc, m) => {
    const rights = m?.rights ?? {};
    (Object.keys(rights) as EventModuleEntityRightsTypes[]).forEach((key) => {
      acc[key] = Boolean(acc[key] || rights[key]);
    });
    return acc;
  }, {});
};

export const eventsTools = {
  sortEventsByDate: (a: EventEntity, b: EventEntity, order: "asc" | "desc" = "asc"): number => {
    // Get all dates and sort them to find the earliest date
    const daysA = a?.dates?.days ?? a?.datesAlt?.days ?? {};
    const daysB = b?.dates?.days ?? b?.datesAlt?.days ?? {};

    const datesA = Object.keys(daysA).sort();
    const datesB = Object.keys(daysB).sort();

    const startA = datesA[0];
    const startB = datesB[0];

    // If both events have no dates, sort by ID
    if (!startA && !startB) {
      return domainTools.sortById(a, b, order);
    }

    // Events with dates come before events without dates
    if (!startA) return 1;
    if (!startB) return -1;

    // Sort by date: in ASC mode, older dates (earlier in time) come first
    if (order === "asc") {
      return startA < startB ? -1 : startA > startB ? 1 : 0;
    } else {
      return startA > startB ? -1 : startA < startB ? 1 : 0;
    }
  },
  sortEventsByDateDesc: (a: EventEntity, b: EventEntity): number =>
    eventsTools.sortEventsByDate(a, b, "desc"),
  /**
   * Checks if the given event has finished by determining if its stage is marked as "COMPLETED".
   *
   * @param {EventEntity} event - The event entity to check.
   * @returns {boolean} Returns true if the event stage is "COMPLETED", otherwise false.
   */
  isFinished: (event: EventEntity): boolean => {
    return event.stage === "COMPLETED";
  },
  convertToDomainEntity: (event: EventOutputView): EventEntity =>
    removeField(
      {
        ...addRights(event),
        modulesRights: concatRightsFromModules(event.modules?.map(addRightsToModule)),
        uiLinks: {
          dashboard: routes.events.pages.event_status.path({
            idOrSlug: event.workspaceId,
            eventId: event.id,
          }),
          schedule_create: routes.events_program.pages.event_schedule_create.path({
            idOrSlug: event.workspaceId,
            eventId: event.id,
          }),
          edit: routes.events.pages.event_edit.path({
            idOrSlug: event.workspaceId,
            eventId: event.id,
          }),
          team: routes.teams.pages.event_team.path({
            idOrSlug: event.workspaceId,
            eventId: event.id,
          }),
        },
      },
      "modules",
    ),
  buildSidebarMenuItems: (
    localeAs: AnyTranslationsFn,
    event: EventEntity | undefined | null,
    workspace: { id?: Identifier; slug?: string } | undefined | null,
    pageInfo?: UseRouteInfo,
    user?: UserTokenValue,
    disableStatusPage?: boolean,
    isPrivateMode?: boolean,
  ): MenuItem[] => {
    const idOrSlug = workspace?.slug ?? workspace?.id;

    if (!idOrSlug || !event?.rights?.canRead) return [];

    return removeUndefined([
      disableStatusPage || isPrivateMode
        ? undefined
        : buildSidebarMenuItem({
            localeAs,
            page: routes.events.pages.event_status,
            matches: {
              idOrSlug,
              eventId: event?.id,
            },
            pageInfo,
          }),
      isPrivateMode
        ? undefined
        : {
            label: localeAs("features.events-program.title"),
            url: "#",
            isActive: true,
            items: removeUndefined([
              event?.modulesRights?.canViewProgramModule &&
              event?.modulesRights?.canListProposalsForPublications
                ? buildSidebarMenuItem({
                    localeAs,
                    page: routes.events_program.pages.event_activities,
                    matches: {
                      idOrSlug,
                      eventId: event?.id,
                    },
                    pageInfo,
                  })
                : undefined,
              buildSidebarMenuItem({
                localeAs,
                page: routes.events_program.pages.event_voting_panel,
                matches: {
                  idOrSlug,
                  eventId: event?.id,
                },
                isDisabled:
                  !event?.modulesRights?.canViewProgramModule ||
                  !event?.modulesRights?.canListProposalsForProgram,
                pageInfo,
              }),
              buildSidebarMenuItem({
                localeAs,
                page: routes.events_program.pages.event_contributors,
                matches: {
                  idOrSlug,
                  eventId: event?.id,
                },
                isDisabled: !event?.modulesRights?.canViewProgramModule,
                pageInfo,
              }),
              buildSidebarMenuItem({
                localeAs,
                page: routes.events_program.pages.event_schedule,
                matches: {
                  idOrSlug,
                  eventId: event?.id,
                },
                isDisabled:
                  !event?.modulesRights?.canViewScheduleModule ||
                  !event?.modulesRights?.canViewScheduleRegularModule ||
                  !event?.modulesRights?.canListProposalsForSchedule,
                pageInfo,
              }),
              event?.modulesRights?.canViewPublicationsModule
                ? buildSidebarMenuItem({
                    localeAs,
                    page: routes.events_program.pages.event_publications,
                    matches: {
                      idOrSlug,
                      eventId: event?.id,
                    },
                    isDisabled:
                      !event?.modulesRights?.canViewPublicationsModule ||
                      !event?.modulesRights?.canListProposalsForPublications,
                    pageInfo,
                  })
                : undefined,
            ]),
          },
      event?.modulesRights?.canViewScheduleBroadcastModule && !isPrivateMode
        ? buildSidebarMenuItem({
            localeAs,
            page: routes.events_program.pages.event_schedule_live,
            matches: {
              idOrSlug,
              eventId: event?.id,
            },
            pageInfo,
          })
        : undefined,
      event?.modulesRights?.canViewScheduleSupportModule
        ? buildSidebarMenuItem({
            localeAs,
            page: routes.events_program.pages.event_support_calendar,
            matches: {
              idOrSlug,
              eventId: event?.id,
            },
            pageInfo,
          })
        : undefined,
      event?.modulesRights?.canViewTeamModule && event?.modulesRights?.canListMembers
        ? buildSidebarMenuItem({
            localeAs,
            page: routes.teams.pages.event_team,
            matches: {
              idOrSlug,
              eventId: event?.id,
            },
            isDisabled:
              !event?.modulesRights?.canViewTeamModule || !event?.modulesRights?.canListMembers,
            pageInfo,
          })
        : undefined,
      event?.modulesRights?.canViewEventReportModule && !isPrivateMode
        ? buildSidebarMenuItem({
            localeAs,
            page: routes.reports.pages.event_report,
            matches: {
              idOrSlug,
              eventId: event?.id,
            },
            pageInfo,
          })
        : undefined,
      event?.modulesRights?.canViewEventActivityFeedbackModule && !isPrivateMode
        ? buildSidebarMenuItem({
            localeAs,
            page: routes.events.pages.event_ratings,
            matches: {
              idOrSlug,
              eventId: event?.id,
            },
            pageInfo,
          })
        : undefined,
      AccessToken.isGlobalAdmin(user)
        ? {
            ...buildSidebarMenuItem({
              localeAs,
              page: routes.events.pages.event_integrations,
              matches: {
                idOrSlug,
                eventId: event?.id,
              },
              pageInfo,
            }),
            isActive: [
              routes.events.pages.event_integrations.pathTemplate,
              routes.events.pages.event_integrations_webhooks.pathTemplate,
              routes.events.pages.event_integrations_personal_tokens.pathTemplate,
            ].includes(pageInfo?.page?.pathTemplate ?? ""),
          }
        : undefined,
      AccessToken.isGlobalAdmin(user) && isPrivateMode && !isProductionEnvironment // TODO remove after implement
        ? buildSidebarMenuItem({
            localeAs,
            page: routes.events.pages.event_users,
            matches: {
              idOrSlug,
              eventId: event?.id,
            },
            isDisabled: true, // TODO turn off when page are ready
            pageInfo,
          })
        : undefined,
    ]) as MenuItem[];
  },
};
